import Highcharts, { TooltipFormatterContextObject } from 'highcharts';

import { formatPercentage } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import { type ScrollConfig } from '../../networkPerformance/slideout/headers/chartHelpers';

// Registers the shared `column2` overlaid-column series type used below.
import 'components/shared/charts/column2Series';

const POINT_WIDTH = 40;

// The fixed chart height (px). Exported so the header can reserve it in MetricDetailLayout's
// loading/empty placeholder — keeps the card from collapsing and jumping when data lands.
export const OVERLAID_COLUMN_CHART_HEIGHT = 280;

type Series = { name: string; values: ReadonlyArray<number | null> };

type OverlaidColumnChartArgs = {
  // Entity names for the x-axis (index-aligned with the series + rates).
  names: ReadonlyArray<string | null>;
  // Rate per entity — drives both the descending sort and the data label on top of each bar.
  rates: ReadonlyArray<number | null>;
  // Grey base column (the total) with the colored overlay column drawn in front of it.
  baseSeries: Series;
  overlaySeries: Series & { color: string };
};

// The "grey total column + colored overlay column, rate label on top, sorted by rate desc" chart
// shared by the Accepted / Selected / Admitted drills. They differ only in which three values they
// read and the overlay color/labels — everything else (layout, sort, data labels, scroll) is
// identical, so it lives here instead of being copy-pasted per header.
export function buildOverlaidColumnChart({ names, rates, baseSeries, overlaySeries }: OverlaidColumnChartArgs): {
  chartOptions: Highcharts.Options;
  scrollConfig: ScrollConfig;
  isEmpty: boolean;
} {
  const order = names.map((_, i) => i).sort((a, b) => (rates[b] ?? 0) - (rates[a] ?? 0));
  const sortedNames = order.map((i) => names[i] || 'None');
  const sortedBase = order.map((i) => baseSeries.values[i] ?? 0);
  const sortedOverlay = order.map((i) => overlaySeries.values[i] ?? 0);
  const sortedRates = order.map((i) => rates[i] ?? null);

  const chartOptions: Highcharts.Options = {
    chart: { type: 'column', style: { fontFamily: textStyles.base.fontFamily }, height: OVERLAID_COLUMN_CHART_HEIGHT },
    accessibility: { enabled: false },
    credits: { enabled: false },
    title: { text: undefined },
    legend: {
      align: 'center',
      verticalAlign: 'bottom',
      symbolRadius: 6,
      itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
    },
    xAxis: {
      categories: sortedNames,
      lineColor: colors.black10,
      tickLength: 0,
      labels: { style: { fontSize: '11px', color: colors.black75 } },
    },
    yAxis: {
      title: { text: undefined },
      gridLineColor: colors.black10,
      labels: { style: { fontSize: '11px', color: colors.black50 } },
    },
    tooltip: { enabled: false },
    plotOptions: {
      column: {
        borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
        pointWidth: POINT_WIDTH,
      },
      // @ts-expect-error column2 is our custom side-by-side series type, not on the plotOptions type
      column2: {
        borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
        pointWidth: POINT_WIDTH,
      },
      series: { animation: false, inactiveOtherPoints: true, events: { legendItemClick: () => false } },
    },
    series: [
      {
        type: 'column',
        name: baseSeries.name,
        color: colors.black10,
        data: sortedBase.map((y, i) => ({
          y,
          label: formatPercentage(sortedRates[i]),
          dataLabels: {
            enabled: true,
            formatter: function (this: TooltipFormatterContextObject): string {
              return `<span style="font-size:10px;font-weight:normal;">${this.point.label}</span>`;
            },
          },
        })),
      },
      { type: 'column2' as 'column', name: overlaySeries.name, color: overlaySeries.color, data: sortedOverlay },
    ],
  };

  const scrollConfig: ScrollConfig = { minWidth: (POINT_WIDTH + POINT_WIDTH * 1.5) * sortedNames.length };
  return { chartOptions, scrollConfig, isEmpty: sortedNames.length === 0 };
}
